import { parseEvolutionPlanExecutions, type EvolutionPlanExecutionRecord } from './evolutionPlanExecution';
import type { EvolutionAttachedPrintable, EvolutionReturnWorkflow } from './evolutionWorkflowTypes';

export { parseEvolutionPlanExecutions, formatEvolutionPlanExecutionSummaryLine } from './evolutionPlanExecution';
export type { EvolutionPlanExecutionRecord };
import { evolutionHasPrintBatchContent } from './mergeEvolutionPrintBatch';

export function normalizeEvolutionProcedureLabels(evo: any): string[] {
  const pp = evo?.procedures_performed;
  if (!Array.isArray(pp)) return [];
  const planIds = new Set(parseEvolutionPlanExecutions(pp).map((p) => p.procedure_execution_id));
  return pp
    .filter((p: any) => !planIds.has(String(p?.procedure_execution_id || '')))
    .map((p: any) => (typeof p === 'string' ? p.trim() : String(p?.name || p?.label || '').trim()))
    .filter(Boolean);
}

export function getEvolutionWorkflowPrintables(evo: any): EvolutionAttachedPrintable[] {
  const raw = evo?.metadata?.evolution_workflow?.printables;
  if (!Array.isArray(raw)) return [];
  return raw.filter((p: unknown) => p && typeof (p as EvolutionAttachedPrintable).templateName === 'string');
}

/** Indicações cirúrgicas persistidas à parte dos impressos (receituário, exames, etc.). */
export function getEvolutionIndicationAttachment(evo: any): EvolutionAttachedPrintable | null {
  const direct = evo?.metadata?.evolution_workflow?.indication;
  if (direct && typeof (direct as EvolutionAttachedPrintable).templateName === 'string') {
    return direct as EvolutionAttachedPrintable;
  }
  const legacy = getEvolutionWorkflowPrintables(evo).find((p) => p.category === 'indication');
  return legacy || null;
}

/** Impressos da evolução, excluindo indicações (seção própria). */
export function getEvolutionNonIndicationPrintables(evo: any): EvolutionAttachedPrintable[] {
  return getEvolutionWorkflowPrintables(evo).filter((p) => p.category !== 'indication');
}

export function splitEvolutionWorkflowAssets(wf: any): {
  printables: EvolutionAttachedPrintable[];
  indication: EvolutionAttachedPrintable | null;
} {
  const printables = getEvolutionWorkflowPrintables({ metadata: { evolution_workflow: wf } });
  const indicationFromField =
    wf?.indication && typeof wf.indication.templateName === 'string'
      ? (wf.indication as EvolutionAttachedPrintable)
      : null;
  const indicationFromPrintables = printables.find((p) => p.category === 'indication') || null;
  return {
    printables: printables.filter((p) => p.category !== 'indication'),
    indication: indicationFromField || indicationFromPrintables,
  };
}

/**
 * Rótulo para listagens sem coluna «tipo» (timeline do paciente, etc.).
 * Receitas antigas só tinham «Simples — fármaco»; assim fica evidente que é um receituário.
 */
export function evolutionPrintableListLabel(p: EvolutionAttachedPrintable): string {
  const raw = String(p.templateName || '').trim();
  if (p.category !== 'prescription') return raw || 'Documento';
  if (!raw) return 'Receituário';
  if (/^receit(u[aá]?rio|a)(\s|$|[—\-–])/i.test(raw)) return raw;
  return `Receituário — ${raw}`;
}

/**
 * Impresso vinculado a uma receita Memed. Impressos anexados antes de gravarmos
 * `linkedDocumentOrigin` são reconhecidos pelo nome com que foram criados.
 */
export function isMemedLinkedPrintable(p: EvolutionAttachedPrintable): boolean {
  if (p.category !== 'prescription' || !p.linkedDocumentId) return false;
  if (p.linkedDocumentOrigin === 'memed') return true;
  return /receita digital memed/i.test(String(p.templateName || ''));
}

export function getEvolutionReturnWorkflow(evo: any): EvolutionReturnWorkflow | null {
  const r = evo?.metadata?.evolution_workflow?.return;
  if (!r || typeof r !== 'object') return null;
  return r as EvolutionReturnWorkflow;
}

/** Retorno combinando checkboxes (metadata) + notas livres na evolução. */
export function formatEvolutionReturnWorkflowLines(wf: EvolutionReturnWorkflow | null): string[] {
  if (!wf) return [];
  const bits: string[] = [];
  if (wf.returnWithExams) bits.push('Retorno com exames');
  if (wf.returnForSurgery) bits.push('Retorno para cirurgia');
  if (wf.dischargeWithRecall) bits.push('Alta — reconvocar depois (recall)');
  if (wf.dischargeNoRecall) bits.push('Alta — não reconvocar');
  const rin = String(wf.returnInDays || '').trim();
  const reeval = String(wf.reevaluationInDays || '').trim();
  const recall = String(wf.recallInDays || '').trim();
  if (rin) bits.push(`Retorno em ${rin} dia(s)`);
  if (reeval) bits.push(`Reavaliação em ${reeval} dia(s)`);
  if (recall) bits.push(`Reconvocação (recall) em ${recall} dia(s)`);
  return bits;
}

export function evolutionViewerHasWorkflowReturn(evo: any): boolean {
  return formatEvolutionReturnWorkflowLines(getEvolutionReturnWorkflow(evo)).length > 0;
}

export function evolutionViewerHasFollowUpSection(evo: any): boolean {
  if (String(evo?.follow_up_notes || '').trim()) return true;
  if (evo?.follow_up_date) return true;
  return evolutionViewerHasWorkflowReturn(evo);
}

/** Monta snapshot «como evolução salva» a partir do formulário aberto (impressão antes de concluir). */
export function buildEvolutionPrintSnapshot(input: {
  form: Record<string, any>;
  patient?: any;
  professional?: any;
  procedureLabels: string[];
  attachedPrintables: EvolutionAttachedPrintable[];
  indicationPrintable: EvolutionAttachedPrintable | null;
  returnWorkflow: EvolutionReturnWorkflow | null;
}): any {
  return {
    ...input.form,
    patient: input.patient ?? input.form.patient,
    professional: input.professional ?? input.form.professional,
    procedures_performed: input.procedureLabels.map((name) => ({ name, notes: '' })),
    metadata: {
      ...(input.form.metadata || {}),
      evolution_workflow: {
        printables: input.attachedPrintables.filter((p) => p.category !== 'indication'),
        indication: input.indicationPrintable,
        return: input.returnWorkflow,
      },
    },
  };
}

export function evolutionHasBatchPrintContent(evo: any): boolean {
  return evolutionHasPrintBatchContent(
    getEvolutionNonIndicationPrintables(evo),
    normalizeEvolutionProcedureLabels(evo),
    getEvolutionIndicationAttachment(evo),
  );
}

/** Há conteúdo para «Documento completo» (evolução + anexos + indicações + retorno). */
export function evolutionHasFullPrintContent(evo: any): boolean {
  if (!evo) return false;
  const vs = evo.vital_signs || {};
  if (Object.values(vs).some((v) => v !== undefined && v !== null && v !== '')) return true;
  if (String(evo.evolution_free_text || '').trim()) return true;
  if (String(evo.subjective || '').trim() || String(evo.objective || '').trim()) return true;
  if (String(evo.assessment || '').trim() || String(evo.plan || '').trim()) return true;
  if (String(evo.diagnosis || '').trim() || String(evo.internal_notes || '').trim()) return true;
  if (evolutionViewerHasFollowUpSection(evo)) return true;
  return evolutionHasPrintBatchContent(
    getEvolutionNonIndicationPrintables(evo),
    normalizeEvolutionProcedureLabels(evo),
    getEvolutionIndicationAttachment(evo),
  );
}
